import Fade from '@mui/material/Fade';
import Popper from '@mui/material/Popper';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { Circle, X } from 'lucide-react';
import { useCallback, useState } from 'react';
import {
  ComposeIcon,
  DeviceTitleText,
  NoConnectionComponent,
  PopperBoxStyle,
  TitleText,
  ValueText,
} from './styles';
import { useTheme } from '@mui/material';
import { DynamicIcon } from 'lucide-react/dynamic';

/**
 * Encapsulates LAN Port info and displays with hover
 * @param event
 */
function PortWithPopper({
  port,
  isPon = false,
}: {
  port: LanPortType | PonPortType;
  isPon?: boolean;
}) {
  const theme = useTheme();

  // Port values
  const portName = isPon
    ? 'PON'
    : ((port?.name?.value as string | undefined) ?? 'port');
  const portStatus = port?.status?.value as string | undefined;
  const portEnabled = port?.enable?.value;
  const portMacAddress = port?.macAddress?.value;

  // Popper states
  const [open, setOpen] = useState(false); // controls the popper displays
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null); // reference to popper's anchor

  const id = open ? 'port-popper-' + (portName || 'unknown') : undefined;

  const isDownOrNoLink = portStatus === 'Down' || portStatus === 'NoLink';

  const portIconStrokeWidth = isDownOrNoLink ? 1.5 : 1.4;

  const circleColor = portEnabled
    ? portStatus === 'CONNECTED'
      ? theme.palette.primary['01']
      : String(portEnabled) == '0'
        ? theme.palette.status.warning
        : theme.palette.error.main
    : theme.palette.status.offline;

  // Sets action when mouse enters the anchor
  const handleMouseEnter = useCallback(
    (event: React.MouseEvent<HTMLElement>) => {
      setAnchorEl(event.currentTarget);
      setOpen(true);
    },
    []
  ); // Dependências vazias

  const handleMouseLeave = useCallback(() => {
    setOpen(false);
  }, []); // Dependências vazias

  return (
    <Stack>
      {/* Anchor to the popper */}
      <Stack
        sx={{
          cursor: 'pointer',
          height: '100%',
        }}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        {/* Compose to add an 'X' to ethernet offline port and displays without when connection it's ok*/}
        <ComposeIcon>
          <DynamicIcon
            name={isPon ? 'circle-dot' : 'ethernet-port'}
            size="38px"
            strokeWidth={portIconStrokeWidth}
            color="currentColor"
          />
          {String(portStatus) !== 'CONNECTED' &&
            String(portEnabled) === '1' && (
              <NoConnectionComponent>
                <X
                  size="18px"
                  color={theme.palette.error.main}
                  strokeWidth={5}
                />
              </NoConnectionComponent>
            )}
        </ComposeIcon>
        <Typography variant="caption" color="currentColor">
          {portName}
        </Typography>
      </Stack>

      {/* Popper linked to the port anchor */}
      <Popper
        id={id}
        open={open}
        anchorEl={anchorEl}
        placement="top"
        sx={{ zIndex: 1300 }}
        transition
      >
        {({ TransitionProps }) => (
          <Fade {...TransitionProps} timeout={300}>
            <PopperBoxStyle>
              <Stack direction="row" gap="8px">
                {portEnabled !== undefined && (
                  <Circle color={circleColor} strokeWidth={4} size="20px" />
                )}
                <DeviceTitleText variant="caption">{portName}</DeviceTitleText>
              </Stack>
              {portMacAddress && (
                <Stack direction="row" spacing={1}>
                  <TitleText variant="caption">MAC:</TitleText>
                  <ValueText>{portMacAddress}</ValueText>
                </Stack>
              )}
              <Stack direction="row" spacing={1} alignItems="center">
                <TitleText variant="caption">Status:</TitleText>
                <ValueText>{portStatus}</ValueText>
              </Stack>
            </PopperBoxStyle>
          </Fade>
        )}
      </Popper>
    </Stack>
  );
}

export default PortWithPopper;
